// poiesis/src/views/format.ts
// Pure display helpers for the chart renderers. No React, no I/O.
import type { ReducedFrame } from './contract'
import { formatDate, getTenantZone } from '../locale'

// 'en' is pinned deliberately for the COMPACT formatter: produces deterministic
// compact output (2.56M / 671) regardless of the machine locale. On a da-DK dev
// box, passing `undefined` yields "2,56 mio." which breaks exact-string tests
// and chart captions. Intentional deviation from using the tenant locale.
const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 })

// Cache locale-specific standard formatters so we don't allocate on every cell render.
const _stdFmtCache = new Map<string, Intl.NumberFormat>()
function stdFmt(locale: string): Intl.NumberFormat {
  let f = _stdFmtCache.get(locale)
  if (!f) {
    f = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 })
    _stdFmtCache.set(locale, f)
  }
  return f
}

/**
 * Compact/adaptive number formatting for chart labels: 2.56M, 1.65M, 671, 12.3.
 * Returns "" for non-finite.
 *
 * When `locale` is supplied (tenant locale, e.g. "da-DK"), uses standard grouping
 * rather than compact notation — correct for table cells where "1.566" (da-DK) must
 * not be confused with the decimal "1.566" in English. Without locale, falls back to
 * compact en formatting (backward-compatible for charts).
 *
 * ONE seam: callers that want locale-aware output pass the tenant locale here;
 * chart renderers pass nothing and keep the compact/en-pinned compact output.
 */
export function formatValue(n: number, locale?: string): string {
  if (!Number.isFinite(n)) return ''
  if (locale) return stdFmt(locale).format(n)
  return COMPACT.format(n)
}

// Locale-aware COMPACT formatters (KPI figures): "3,07 mio." (da-DK) /
// "3.07M" (en). Distinct from COMPACT above, whose 'en' pin exact-string
// tests depend on. Cached like stdFmt.
const _compactFmtCache = new Map<string, Intl.NumberFormat>()
function compactFmt(locale: string): Intl.NumberFormat {
  let f = _compactFmtCache.get(locale)
  if (!f) {
    f = new Intl.NumberFormat(locale, { notation: 'compact', maximumSignificantDigits: 3 })
    _compactFmtCache.set(locale, f)
  }
  return f
}

/** SEN-226: the fraction digits a CALCULATED value shows, by magnitude —
 *  whole numbers from 100 up, one decimal from 10, two below (99.9245… →
 *  "99.9", 1 964.3 → "1,964", 3.14159 → "3.14"). A maximum, never padded: a
 *  whole number stays whole. Only computed values: a STORED number shows what
 *  was saved (formatValue, ≤2 decimals — 149.99 stays 149.99), and charts and
 *  KPI tiles keep formatValue's precision. */
export function calculatedFractionDigits(n: number): number {
  const a = Math.abs(n)
  return a >= 100 ? 0 : a >= 10 ? 1 : 2
}

const _calcFmtCache = new Map<string, Intl.NumberFormat>()

/** SEN-226: a calculated cell's number — tenant-locale grouping, decimals by
 *  magnitude (calculatedFractionDigits). Returns "" for non-finite. */
export function formatCalculatedValue(n: number, locale?: string): string {
  if (!Number.isFinite(n)) return ''
  const digits = calculatedFractionDigits(n)
  const key = `${locale ?? ''}|${digits}`
  let f = _calcFmtCache.get(key)
  if (!f) {
    f = new Intl.NumberFormat(locale, { maximumFractionDigits: digits })
    _calcFmtCache.set(key, f)
  }
  return f.format(n)
}

/** SEN-1790: a KPI figure. Values under a million keep full locale grouping
 *  (precision is the point of a KPI, and "415.265,5" still fits a tile);
 *  from 1M up the figure compacts ("3,07 mio." / "3.07M") so a
 *  portfolio-scale sum never clips its own unit off the tile — the EXACT
 *  value stays in the tile's title/tooltip (StatRenderer already carries it). */
export function formatStatValue(n: number, locale?: string): string {
  if (!Number.isFinite(n)) return ''
  if (Math.abs(n) < 1_000_000) return formatValue(n, locale)
  return locale ? compactFmt(locale).format(n) : COMPACT.format(n)
}

type UnitComposition = ReducedFrame['meta']['unit_composition']
type Term = { ref_kind: 'unit' | 'type' | 'attribute'; ref: string; power: number }

/** Compose a unit string from unit_composition → "kWh", "kWh/m²·year", or "".
 *  Empty when there are no terms or the frame reports mixed units. */
export function formatUnit(uc: UnitComposition | null | undefined): string {
  if (!uc || 'mixed' in uc) return ''
  const term = (t: Term) => (t.power === 1 ? t.ref : `${t.ref}^${t.power}`)
  const num = (uc.numerator ?? []).map(term).join('·')
  const den = (uc.denominator ?? []).map(term).join('·')
  if (!num && !den) return ''
  if (!den) return num
  return `${num || '1'}/${den}`
}

/** SEN-1796 — the DECLARED unit of a cell, resolved in precedence order:
 *  1. the value dict's own `unit` (most specific — the wire said what this
 *     particular value is measured in; gallery samples and agent writes carry it),
 *  2. the attribute config's `unit_composition`, through the SAME formatUnit
 *     the chart captions use,
 *  3. (SEN-226) a calculated attribute's formula's `unit_composition`
 *     (`config.formula.unit_composition`: kWh, kWh/m²) — the unit the
 *     authoring gate checked the formula against.
 *  One source renders — never both — and a plain number stays plain ('').
 *  No conversion: the unit is a caption on the number the substrate returned. */
export function declaredUnit(value: unknown, config?: Record<string, unknown>): string {
  // 0. SEN-433 (A8): the metric's declared display unit (config.unit_label,
  //    projected from MetricDefinition.unit_label) — how a person writes it
  //    ("kWh/m²/y", where the composition renders "kWh/m²·year"). Every
  //    surface reads it here, and the email snapshot reads the same key
  //    (monad/dashboards/snapshot.py _unit_label): one measure, one unit.
  const label = config?.['unit_label']
  if (typeof label === 'string' && label) return label
  if (typeof value === 'object' && value !== null) {
    const u = (value as Record<string, unknown>)['unit']
    if (typeof u === 'string' && u) return u
  }
  const own = formatUnit(config?.['unit_composition'] as UnitComposition | undefined)
  if (own) return own
  const formula = config?.['formula']
  return typeof formula === 'object' && formula !== null
    ? formatUnit((formula as Record<string, unknown>)['unit_composition'] as UnitComposition | undefined)
    : ''
}

/** The SERVED display figure of a measure column (`meta.columns[].display`): the value re-expressed on its
 *  unit's display ladder by the server ("276 GWh" for 276 M kWh). The card shows it as given - no scaling
 *  here (the no-frame-arithmetic rule). Null when the server kept the figure in its own unit. */
export function servedDisplay(
  columns: ReadonlyArray<{ name: string; display?: { value: number; unit: string } | null }> | null | undefined,
  column: string,
): { value: number; unit: string } | null {
  return columns?.find((c) => c.name === column)?.display ?? null
}

/** Round-step tick positions covering [lo, hi]. Steps are 1/2/2.5/5 × 10^k (F1, SEN-1398). */
export function niceTicks(lo: number, hi: number, n = 5): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1]
  if (lo === hi) hi = lo + 1
  const span = hi - lo
  const rawStep = span / Math.max(1, n - 1)
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const norm = rawStep / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag
  const start = Math.floor(lo / step) * step
  const ticks: number[] = []
  for (let t = start; t <= hi + step / 2; t += step) {
    ticks.push(Math.abs(t) < step / 1e6 ? 0 : Number(t.toPrecision(12)))
  }
  return ticks
}

/** Axis-tick number label: SI-compact via the pinned-en COMPACT formatter. */
export function formatTick(n: number): string {
  return formatValue(n)
}

/** Evenly spaced time ticks over [minMs, maxMs] labeled by span granularity:
 *  >2y → years, >60d → months, else days -- through THE date formatter
 *  (poiesis/src/locale.ts: the tenant's locale), read on the tenant zone
 *  (SEN-1761: bucket instants are the tenant's local midnights; labelling them
 *  at pinned UTC printed the PREVIOUS day east of UTC). */
export function timeTicks(minMs: number, maxMs: number, n = 6): { t: number; label: string }[] {
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs <= minMs) return []
  const span = maxMs - minMs
  const style: Intl.DateTimeFormatOptions = span > 2 * 365 * 864e5 ? { year: 'numeric' }
    : span > 60 * 864e5 ? { month: 'short' } : { day: 'numeric', month: 'short' }
  const zone = getTenantZone()
  const count = Math.max(2, n)
  const out: { t: number; label: string }[] = []
  for (let i = 0; i < count; i++) {
    const t = minMs + (span * i) / (count - 1)
    out.push({ t, label: formatDate(t, style, { zone }) })
  }
  return out
}

/** SEN-1769: the fraction of the data span padded onto EACH side in `data`
 *  mode. 7.5% sits in the middle of the spec's 5–10% band: enough that the
 *  extreme marks are not clipped by the plot edge, little enough that the
 *  cloud still fills the canvas. */
const DATA_PAD = 0.075

/** Snap a data extent to the round tick extent (Vega/Plot-style nice domain):
 *  the axis' outermost gridlines always bound the marks. Scale-domain math —
 *  tick DRAWING stays in Axis.tsx (shared-primitives guard).
 *
 *  `mode` is the AXIS BASELINE POLICY the view kind declares
 *  (ViewKindSpec.axis_baseline → GET /view-kinds):
 *   - 'zero' (default, unchanged): the caller's extent already includes zero;
 *     snap outward to round ticks.
 *   - 'data': frame the DATA — pad the extent by DATA_PAD of its span on each
 *     side, widen a degenerate (zero-span) range symmetrically first, then snap
 *     outward. Never re-introduces zero. */
export function niceDomain(
  lo: number,
  hi: number,
  n = 5,
  mode: 'zero' | 'data' = 'zero',
): [number, number] {
  if (mode === 'data') {
    let a = Math.min(lo, hi)
    let b = Math.max(lo, hi)
    if (!Number.isFinite(a) || !Number.isFinite(b)) return [0, 1]
    if (b === a) {
      // Degenerate: widen symmetrically. 5% of the magnitude, or ±0.5 at zero.
      const w = Math.abs(a) > 0 ? Math.abs(a) * 0.05 : 0.5
      a -= w
      b += w
    }
    const span = b - a
    const padded: [number, number] = [a - span * DATA_PAD, b + span * DATA_PAD]
    const ticks = niceTicks(padded[0], padded[1], n)
    return [
      Math.min(padded[0], ticks[0] ?? padded[0]),
      Math.max(padded[1], ticks[ticks.length - 1] ?? padded[1]),
    ]
  }
  const ticks = niceTicks(lo, hi, n)
  return [Math.min(lo, ticks[0] ?? lo), Math.max(hi, ticks[ticks.length - 1] ?? hi)]
}
